import type { DesktopPerformanceMode } from './desktop-performance-mode';

export const DEFAULT_DESKTOP_IFRAME_CAP = 2;

export type DesktopBackgroundMode = 'flat' | 'grid' | 'mesh' | 'cheap';

export interface ThermalWindowInput {
	id: string;
	appId: string;
	minimized: boolean;
	focused: boolean;
	meta?: Record<string, unknown>;
}

export interface DesktopThermalSnapshot {
	updatedAt: number;
	performanceMode: DesktopPerformanceMode;
	backgroundMode: DesktopBackgroundMode;
	windowCount: number;
	visibleWindowCount: number;
	minimizedWindowCount: number;
	iframeWindowCount: number;
	visibleIframeWindowCount: number;
	activeIframeCount: number;
	parkedIframeCount: number;
	focusedWindowId: string | null;
	focusedAppId: string | null;
	iframeCap: number;
	capExceeded: boolean;
}

export function inferWindowRuntimeKind(win: { meta?: Record<string, unknown> }): 'native' | 'iframe' | 'image' | 'v2-iframe' {
	const kind = win.meta?.runtimeKind;
	if (kind === 'iframe' || kind === 'image' || kind === 'v2-iframe') return kind;
	if (typeof win.meta?.launchUrl === 'string') return 'iframe';
	return 'native';
}

export function isIframeRuntimeWindow(win: { meta?: Record<string, unknown> }): boolean {
	const kind = inferWindowRuntimeKind(win);
	return kind === 'iframe' || kind === 'image' || kind === 'v2-iframe';
}

function isParkedIframeWindow(win: ThermalWindowInput): boolean {
	return win.meta?.framePaused === true || win.meta?.iframeParked === true || win.meta?.parked === true;
}

export function buildDesktopThermalSnapshot({
	windows,
	performanceMode,
	backgroundMode,
	iframeCap = DEFAULT_DESKTOP_IFRAME_CAP,
	now = () => Date.now(),
}: {
	windows: ThermalWindowInput[];
	performanceMode: DesktopPerformanceMode;
	backgroundMode: DesktopBackgroundMode;
	iframeCap?: number;
	now?: () => number;
}): DesktopThermalSnapshot {
	const visibleWindows = windows.filter((win) => !win.minimized);
	const iframeWindows = windows.filter(isIframeRuntimeWindow);
	const visibleIframeWindows = iframeWindows.filter((win) => !win.minimized);
	const parkedIframeWindows = iframeWindows.filter(isParkedIframeWindow);
	const focused = windows.find((win) => win.focused) ?? null;
	const activeIframeCount = visibleIframeWindows.filter((win) => !isParkedIframeWindow(win)).length;

	return {
		updatedAt: now(),
		performanceMode,
		backgroundMode,
		windowCount: windows.length,
		visibleWindowCount: visibleWindows.length,
		minimizedWindowCount: windows.length - visibleWindows.length,
		iframeWindowCount: iframeWindows.length,
		visibleIframeWindowCount: visibleIframeWindows.length,
		activeIframeCount,
		parkedIframeCount: parkedIframeWindows.length,
		focusedWindowId: focused?.id ?? null,
		focusedAppId: focused?.appId ?? null,
		iframeCap,
		capExceeded: activeIframeCount > iframeCap,
	};
}

interface ThermalSnapshotTarget {
	document?: Document;
	window?: Window;
}

type ThermalWindowWithGetter = Window & {
	__khalDesktopThermalSnapshot?: () => DesktopThermalSnapshot;
};

function makeSnapshotEvent(snapshot: DesktopThermalSnapshot, targetDocument: Document, targetWindow: Window): Event {
	const CustomEventCtor = (targetWindow as unknown as { CustomEvent?: typeof CustomEvent }).CustomEvent;
	if (typeof CustomEventCtor === 'function') {
		return new CustomEventCtor('khal:desktop-thermal-snapshot', { detail: snapshot });
	}
	const event = targetDocument.createEvent('CustomEvent');
	event.initCustomEvent('khal:desktop-thermal-snapshot', false, false, snapshot);
	return event;
}

export function publishDesktopThermalSnapshot(
	snapshot: DesktopThermalSnapshot,
	{ document: targetDocument = document, window: targetWindow = window }: ThermalSnapshotTarget = {}
) {
	const root = targetDocument.documentElement;
	root.dataset.desktopPerformance = snapshot.performanceMode;
	root.dataset.khalBackgroundMode = snapshot.backgroundMode;
	root.dataset.khalWindowCount = String(snapshot.windowCount);
	root.dataset.khalVisibleWindowCount = String(snapshot.visibleWindowCount);
	root.dataset.khalMinimizedWindowCount = String(snapshot.minimizedWindowCount);
	root.dataset.khalIframeCount = String(snapshot.iframeWindowCount);
	root.dataset.khalVisibleIframeCount = String(snapshot.visibleIframeWindowCount);
	root.dataset.khalActiveIframeCount = String(snapshot.activeIframeCount);
	root.dataset.khalParkedIframeCount = String(snapshot.parkedIframeCount);
	root.dataset.khalIframeCap = String(snapshot.iframeCap);
	root.dataset.khalIframeCapExceeded = String(snapshot.capExceeded);

	const thermalWindow = targetWindow as ThermalWindowWithGetter;
	thermalWindow.__khalDesktopThermalSnapshot = () => snapshot;
	targetWindow.dispatchEvent(makeSnapshotEvent(snapshot, targetDocument, targetWindow));
}
